import { useTranslation } from 'react-i18next';

type Chapter = {
  no: string;
  headingKey: string;
  paraKeys: string[];
  img: string;
  imgAltKey?: string;
};

const chapters: Chapter[] = [
  { no: '01', headingKey: 'about.us.h3.1', paraKeys: ['about.us.para1'], img: '/images/generated/hero-butler-2026.jpg' },
  { no: '02', headingKey: 'about.us.h3.2', paraKeys: ['about.us.para2'], img: '/images/generated/tjba26_b05_glass_polishing_r2.jpg', imgAltKey: 'about.us.chapter2.imgAlt' },
  { no: '03', headingKey: 'about.us.h3.3', paraKeys: ['about.us.para3', 'about.us.para4'], img: '/images/generated/tjba26_a06_bedmaking_practice_r3.jpg', imgAltKey: 'about.us.chapter3.imgAlt' },
  { no: '04', headingKey: 'about.us.h3.4a', paraKeys: ['about.us.para5'], img: '/images/generated/materials-hero-2026.jpg' },
  { no: '05', headingKey: 'about.us.h3.4b', paraKeys: ['about.us.para6'], img: '/images/generated/tjba26_b11_entrance_bow_female.jpg', imgAltKey: 'about.us.chapter5.imgAlt' },
];

export default function AboutUs() {
  const { t } = useTranslation();
  return (
    <section className="relative py-20 md:py-28 bg-background-50">
      <div className="max-w-[1280px] mx-auto px-6 sm:px-8 md:px-12 lg:px-16">
        {/* Section header */}
        <div className="text-center mb-16 md:mb-20">
          <p className="text-accent-600 text-[13px] tracking-[0.22em] mb-5 font-label">
            OUR STORY
          </p>
          <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 tracking-[0.06em] mb-3">
            {t('about.us.title')}
          </h2>
          {String(t('about.us.subtitleEn')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="section-title-en text-accent-500 text-[15px] md:text-base tracking-[0.12em] mb-5">{__s}</p>))}
          <div className="gold-line mx-auto mb-6"></div>
          {String(t('about.us.lead')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[17px] md:text-[18px] text-foreground-600 tracking-[0.03em] leading-[1.9] max-w-[640px] mx-auto">{__s}</p>))}
        </div>

        {/* Alternating illustrated chapters */}
        <div className="space-y-16 md:space-y-24">
          {chapters.map((chapter, idx) => {
            const reverse = idx % 2 === 1;
            return (
              <div
                key={chapter.headingKey}
                className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center"
              >
                {/* Image */}
                <div className={`lg:col-span-6 ${reverse ? 'lg:order-2' : 'lg:order-1'}`}>
                  <div className="relative">
                    <div className="absolute -inset-2 border border-accent-500/20 pointer-events-none hidden md:block"></div>
                    <img
                      src={chapter.img}
                      alt={t(chapter.imgAltKey ?? chapter.headingKey)}
                      className="relative w-full aspect-[4/3] object-cover object-top"
                      style={chapter.img.includes('tjba26_a06_') ? { objectPosition: '50% 100%' } : undefined}
                      loading="lazy"
                    />
                  </div>
                </div>

                {/* Text */}
                <div className={`lg:col-span-6 ${reverse ? 'lg:order-1' : 'lg:order-2'}`}>
                  <div className="flex items-baseline gap-4 mb-4">
                    <span className="section-title-en text-accent-500/50 text-[34px] md:text-[40px] leading-none flex-shrink-0">
                      {chapter.no}
                    </span>
                    <span className="w-10 h-px bg-accent-500/50 translate-y-[-6px]"></span>
                  </div>
                  <h3 className="font-heading text-[21px] md:text-[25px] text-foreground-900 tracking-[0.05em] leading-[1.6] mb-5">
                    {t(chapter.headingKey)}
                  </h3>
                  <div className="space-y-4">
                    {chapter.paraKeys.map((key) => (
                      <p
                        key={key}
                        className="text-[16px] md:text-[17px] text-foreground-600 tracking-[0.03em] leading-[2.05]"
                      >
                        {t(key)}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
